// src/components/activities/CreateActivityDrawer.tsx
import React, { useEffect, useState } from 'react';
import {
  Drawer,
  Form,
  Input,
  Button,
  Select,
  Switch,
  Upload,
  message,
  Radio,
  DatePicker,
  Skeleton,
  InputNumber,
  Popconfirm,
} from 'antd';
import type { UploadFile } from 'antd/es/upload/interface';
import dayjs, { Dayjs } from 'dayjs';
import {
  InboxOutlined,
  EyeOutlined,
  EyeInvisibleOutlined,
  DeleteOutlined,
} from '@ant-design/icons';

import {
  createActivity,
  uploadActivityFiles,
  type AppointmentInput,
  type ActivityTypeCode,
  type CreateActivityRequest,
  getActivity,
  updateActivity,
  updateAppointment,
  deleteActivity,
} from '../../services/activities';
import { listCustomers } from '../../services/customers';
import { getTeam } from '../../services/users';
import { listActivityTypes, type ActivityTypeDTO } from '../../services/activityTypes';
import { renderAntdIcon } from '../../utils/antdIcon';
import FileManager from '../files/FileManager';

type Mode = 'create' | 'edit';

type Props = {
  open: boolean;
  onClose: () => void;
  onCreated?: () => void;
  onUpdated?: () => void;
  defaultCustomerId?: number;
  defaultCustomerLabel?: string;

  // create helpers
  forceType?: ActivityTypeCode;
  lockType?: boolean;
  presetAppointment?: {
    startAt?: string;
    endAt?: string;
    confirmed?: boolean;
    allDay?: boolean;
    location?: string;
    title?: string;
  };

  // edit helpers
  mode?: Mode;
  activityId?: number;
};

const { TextArea } = Input;

type FormValues = {
  name?: string;
  description?: string;
  customerId?: number;
  occurredAt?: string;
  visibility?: boolean;
  type?: ActivityTypeCode;
  appointment?: AppointmentInput & { videomakerId?: number | null };

  // ▼ override SOLO per EXTRA
  durationMinutes?: number | null;
  cost?: number | null;
};

const CreateActivityDrawer: React.FC<Props> = ({
  open,
  onClose,
  onCreated,
  onUpdated,
  defaultCustomerId,
  defaultCustomerLabel,
  forceType,
  lockType = false,
  presetAppointment,
  mode = 'create',
  activityId,
}) => {
  const [form] = Form.useForm<FormValues>();
  const [loading, setLoading] = useState(false);

  const [customers, setCustomers] = useState<{ label: string; value: number }[]>([]);
  const [loadingCustomers, setLoadingCustomers] = useState(false);

  const [files, setFiles] = useState<UploadFile[]>([]);
  const customerLocked = !!defaultCustomerId;

  const [videomakerOptions, setVideomakerOptions] = useState<{ label: string; value: number }[]>([]);
  const [loadingVideomakers, setLoadingVideomakers] = useState(false);

  const [loadingTypes, setLoadingTypes] = useState(false);
  const [typeOptions, setTypeOptions] = useState<ActivityTypeDTO[]>([]);

  const watchedType = Form.useWatch('type', form);
  const isShooting = watchedType === 'MEDIA_SHOOT';
  const isExtra = watchedType === 'EXTRA';
  const apptStartIso = Form.useWatch(['appointment', 'startAt'], form) as string | undefined;

  const [initialSnapshot, setInitialSnapshot] = useState<any>(null);
  const [dirty, setDirty] = useState(false);

  // Init al mount/apertura
  useEffect(() => {
    if (!open) return;

    // clienti
    if (defaultCustomerId) {
      form.setFieldsValue({ customerId: defaultCustomerId });
      const label = defaultCustomerLabel || `Cliente #${defaultCustomerId}`;
      setCustomers([{ label, value: defaultCustomerId }]);
      setLoadingCustomers(false);
    } else {
      setLoadingCustomers(true);
      listCustomers()
        .then((items) => {
          setCustomers(
            (items || []).map((c: any) => ({
              label: c.businessName || c.name || c.email || `Cliente #${c.id}`,
              value: c.id,
            })),
          );
        })
        .catch(() => setCustomers([]))
        .finally(() => setLoadingCustomers(false));
    }

    // tipi
    setLoadingTypes(true);
    listActivityTypes(true)
      .then((list) => {
        const enabled = Array.isArray(list) ? list.filter((t) => t.active) : [];
        setTypeOptions(enabled);

        if (mode === 'create') {
          let defaultType = enabled.find((t) => t.code === 'EXTRA')?.code || enabled[0]?.code;
          if (forceType) defaultType = forceType;

          const initial: Partial<FormValues> = {
            visibility: true,
            type: defaultType,
            name: '',
            occurredAt: presetAppointment?.startAt || dayjs().toISOString(),
          };

          if (defaultType === 'MEDIA_SHOOT') {
            const start = presetAppointment?.startAt || dayjs().toISOString();
            const end = presetAppointment?.endAt || dayjs(start).add(2, 'hour').toISOString();
            initial.appointment = {
              title: presetAppointment?.title || 'Shooting',
              description: '',
              location: presetAppointment?.location,
              startAt: start,
              endAt: end,
              allDay: presetAppointment?.allDay ?? false,
              confirmed: presetAppointment?.confirmed ?? true,
              cancelled: false,
            } as any;
            initial.occurredAt = start;
          }

          form.setFieldsValue(initial as any);
          setInitialSnapshot(form.getFieldsValue(true));
        }
      })
      .catch(() => setTypeOptions([]))
      .finally(() => setLoadingTypes(false));

    // videomaker
    setLoadingVideomakers(true);
    getTeam()
      .then((list) => {
        const opts = (list || [])
          .filter((u) => (u.role || '').toUpperCase() === 'VIDEOMAKER')
          .map((u) => ({
            value: u.id,
            label: `${(u.firstName ?? '').trim()} ${(u.lastName ?? '').trim()}`.trim() || u.email,
          }));
        setVideomakerOptions(opts);
      })
      .catch(() => setVideomakerOptions([]))
      .finally(() => setLoadingVideomakers(false));

    // EDIT: carica dati
    (async () => {
      if (open && mode === 'edit' && activityId) {
        try {
          setLoading(true);
          const a = await getActivity(activityId);
          form.setFieldsValue({
            type: a.activityType?.code || a.type || 'MEDIA_SHOOT',
            name: a.name,
            description: a.description || undefined,
            customerId: a.customerId || undefined,
            occurredAt: a.occurredAt,
            visibility: a.visibility,
            appointment: a.appointment
              ? {
                  title: a.appointment.title,
                  description: a.appointment.description || undefined,
                  location: a.appointment.location || undefined,
                  startAt: a.appointment.startAt,
                  endAt: a.appointment.endAt || undefined,
                  allDay: a.appointment.allDay,
                  confirmed: a.appointment.confirmed,
                  cancelled: a.appointment.cancelled,
                  videomakerId: a.appointment.videomakerId ?? null,
                }
              : undefined,
            // ▼ carichiamo gli EFFETTIVI dal BE (se EXTRA, sono override)
            durationMinutes: a.durationMinutes ?? undefined,
            cost: a.cost ?? undefined,
          } as any);
          setInitialSnapshot(form.getFieldsValue(true));
        } catch (e: any) {
          message.error(e?.message || 'Errore caricamento attività');
          onClose();
        } finally {
          setLoading(false);
        }
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, mode, activityId]);

  // Reset al close
  useEffect(() => {
    if (!open) {
      form.resetFields();
      setFiles([]);
      setTypeOptions([]);
      setInitialSnapshot(null);
      setDirty(false);
    }
  }, [open, form]);

  const onValuesChange = () => {
    const now = form.getFieldsValue(true);
    setDirty(JSON.stringify(now) !== JSON.stringify(initialSnapshot));
  };

  // Autofill nome per tipi non EXTRA/MEDIA_SHOOT
  useEffect(() => {
    if (!watchedType) return;
    const t = typeOptions.find((x) => x.code === watchedType);
    if (!t) return;

    if (watchedType !== 'EXTRA' && watchedType !== 'MEDIA_SHOOT') {
      if (!form.getFieldValue('name')) {
        form.setFieldsValue({ name: t.label });
      }
      // pulisci override se si cambia da EXTRA a qualcos'altro
      if (watchedType !== 'EXTRA') {
        form.setFieldsValue({ durationMinutes: undefined, cost: undefined });
      }
    } else if (watchedType === 'EXTRA') {
      if (!form.getFieldValue('name')) {
        form.setFieldsValue({ name: '' });
      }
    }
  }, [watchedType, typeOptions, form]);

  // se shooting, tieni occurredAt = startAt
  useEffect(() => {
    if (watchedType !== 'MEDIA_SHOOT') return;
    if (apptStartIso) {
      form.setFieldsValue({ occurredAt: apptStartIso });
    }
  }, [watchedType, apptStartIso, form]);

  const getTypeLabel = (code?: string) =>
    typeOptions.find((t) => t.code === code)?.label || '';

  // --- SAVE HANDLERS ---
  const handleCreate = async () => {
    try {
      const values = await form.validateFields();
      const computedName =
        values.type === 'MEDIA_SHOOT'
          ? values.appointment?.title || 'Shooting'
          : values.type && values.type !== 'EXTRA'
            ? getTypeLabel(values.type)
            : values.name;

      let payload: CreateActivityRequest;

      if (values.type === 'MEDIA_SHOOT') {
        if (!values.appointment?.startAt)
          throw new Error('Per lo shooting è richiesta la data/ora di inizio.');
        if (!values.appointment?.videomakerId) throw new Error('Seleziona il videomaker.');

        payload = {
          type: 'MEDIA_SHOOT',
          name: computedName,
          description: values.description || undefined,
          customerId: values.customerId!,
          occurredAt: values.appointment.startAt,
          visibility: values.visibility ?? true,
          appointment: {
            title: values.appointment.title || 'Shooting',
            description: values.appointment.description,
            location: values.appointment.location,
            startAt: values.appointment.startAt,
            endAt: values.appointment.endAt,
            videomakerId: values.appointment.videomakerId,
            confirmed: values.appointment.confirmed ?? true,
            allDay: values.appointment.allDay ?? false,
            cancelled: values.appointment.cancelled ?? false,
          } as any,
        };
      } else if (values.type === 'EXTRA') {
        payload = {
          type: 'EXTRA',
          name: computedName || '',
          description: values.description || undefined,
          customerId: values.customerId!,
          occurredAt: values.occurredAt,
          visibility: values.visibility ?? true,
          // ▼ override SOLO per EXTRA
          durationMinutes: values.durationMinutes ?? null,
          cost: values.cost ?? null,
        };
      } else {
        payload = {
          type: values.type as Exclude<ActivityTypeCode, 'EXTRA' | 'MEDIA_SHOOT'>,
          name: computedName,
          description: values.description || undefined,
          customerId: values.customerId!,
          occurredAt: values.occurredAt,
          visibility: values.visibility ?? true,
        };
      }

      setLoading(true);
      const created = await createActivity(payload);

      const binFiles = files.map((f) => f.originFileObj).filter(Boolean) as File[];
      if (created?.id && binFiles.length > 0) {
        await uploadActivityFiles(created.id, binFiles);
      }

      message.success('Attività creata con successo');
      onCreated?.();
      onClose();
    } catch (err: any) {
      if (err?.errorFields) return;
      message.error(err?.message || 'Errore durante la creazione');
    } finally {
      setLoading(false);
    }
  };

  const handleUpdate = async () => {
    if (!activityId) return;
    try {
      const values = await form.validateFields();
      setLoading(true);

      // Aggiorna campi base + override se EXTRA
      await updateActivity(activityId, {
        name: values.name,
        description: values.description,
        customerId: values.customerId!,
        occurredAt: values.occurredAt,
        visibility: values.visibility,
        ...(values.type === 'EXTRA'
          ? {
              durationMinutes: values.durationMinutes ?? null,
              cost: values.cost ?? null,
            }
          : {}),
      });

      // Aggiorna appointment se MEDIA_SHOOT
      if (values.type === 'MEDIA_SHOOT' && values.appointment) {
        await updateAppointment(activityId, {
          title: values.appointment.title,
          description: values.appointment.description,
          location: values.appointment.location,
          startAt: values.appointment.startAt,
          endAt: values.appointment.endAt,
          allDay: values.appointment.allDay,
          confirmed: values.appointment.confirmed,
          cancelled: values.appointment.cancelled,
          videomakerId: values.appointment.videomakerId,
        });
      }

      message.success('Modifiche salvate');
      onUpdated?.();
      onClose();
    } catch (err: any) {
      if (err?.errorFields) return;
      message.error(err?.message || 'Errore durante il salvataggio');
    } finally {
      setLoading(false);
    }
  };

  // --- DELETE HANDLER ---
  const handleDelete = async () => {
    if (!activityId) return;
    try {
      setLoading(true);
      await deleteActivity(activityId);
      message.success('Attività eliminata');
      onUpdated?.();
      onClose();
    } catch (err: any) {
      message.error(err?.message || 'Errore durante l’eliminazione');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = () => (mode === 'edit' ? handleUpdate() : handleCreate());

  const titleRightButton = (
    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
      {mode === 'edit' && (
        <Popconfirm
          title="Sei sicuro di voler eliminare questa attività?"
          okText="Elimina"
          cancelText="Annulla"
          onConfirm={handleDelete}
        >
          <Button danger icon={<DeleteOutlined />}>
            Elimina
          </Button>
        </Popconfirm>
      )}
      <Button
        type="primary"
        onClick={handleSubmit}
        loading={loading}
        disabled={mode === 'edit' ? !dirty : false}
      >
        {mode === 'edit' ? 'Salva modifiche' : 'Salva Attività'}
      </Button>
    </div>
  );

  return (
    <Drawer
      title={
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 12,
            justifyContent: 'space-between',
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <span style={{ fontSize: 16, fontWeight: 600 }}>
              {mode === 'edit' ? 'Modifica Attività' : 'Nuova Attività'}
            </span>
          </div>
          {titleRightButton}
        </div>
      }
      open={open}
      onClose={onClose}
      width={620}
      destroyOnClose
    >
      <Form form={form} layout="vertical" size="large" onValuesChange={onValuesChange}>
        {/* Tipo Attività */}
        <Form.Item
          name="type"
          label={<span style={{ fontWeight: 600, fontSize: 14 }}>Tipo di Attività</span>}
          rules={[{ required: true, message: 'Seleziona il tipo di attività' }]}
        >
          {loadingTypes ? (
            <Skeleton active paragraph={{ rows: 2 }} />
          ) : lockType || mode === 'edit' ? (
            <div
              style={{
                height: 56,
                borderRadius: 8,
                border: '1px solid #1677ff',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 8,
                background: '#e6f4ff',
                fontWeight: 500,
              }}
            >
              {renderAntdIcon(typeOptions.find((o) => o.code === form.getFieldValue('type'))?.icon)}
              <span>
                {typeOptions.find((o) => o.code === form.getFieldValue('type'))?.label ||
                  form.getFieldValue('type')}
              </span>
            </div>
          ) : (
            <Radio.Group
              buttonStyle="solid"
              style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))',
                gap: 12,
                width: '100%',
              }}
            >
              {typeOptions.map((opt) => {
                const selected = form.getFieldValue('type') === opt.code;
                return (
                  <Radio.Button
                    key={opt.id}
                    value={opt.code}
                    style={{
                      height: 56,
                      borderRadius: 8,
                      border: `1px solid ${selected ? '#1677ff' : '#d9d9d9'}`,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontWeight: 500,
                      background: selected ? '#e6f4ff' : '#fff',
                      color: selected ? '#1677ff' : undefined,
                    }}
                  >
                    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 6 }}>
                      <div style={{ fontSize: 16, lineHeight: 1 }}>
                        {renderAntdIcon(opt.icon)}
                      </div>
                      <div style={{ fontSize: 12, lineHeight: 1.5, textAlign: 'center' }}>
                        {opt.label}
                      </div>
                    </div>
                  </Radio.Button>
                );
              })}
            </Radio.Group>
          )}
        </Form.Item>

        {/* Shooting */}
        {isShooting && (
          <>
            <div
              style={{
                padding: 12,
                border: '1px solid #e6f4ff',
                background: '#f0f9ff',
                borderRadius: 8,
                marginBottom: 16,
              }}
            >
              <div style={{ fontWeight: 700, color: '#1677ff', marginBottom: 8 }}>Dettagli Shooting</div>

              <Form.Item
                name={['appointment', 'title']}
                label={<span style={{ fontWeight: 600, fontSize: 14 }}>Titolo Shooting</span>}
                rules={[{ required: true, message: 'Inserisci un titolo' }]}
              >
                <Input placeholder="Es. Shooting menù autunno" style={{ height: 40, borderRadius: 6 }} />
              </Form.Item>

              <Form.Item
                name={['appointment', 'location']}
                label={<span style={{ fontWeight: 600, fontSize: 14 }}>Luogo</span>}
              >
                <Input
                  placeholder="Es. Ristorante XYZ (Via Roma 10, Milano)"
                  style={{ height: 40, borderRadius: 6 }}
                />
              </Form.Item>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                <Form.Item
                  name={['appointment', 'startAt']}
                  label={<span style={{ fontWeight: 600, fontSize: 14 }}>Inizio</span>}
                  rules={[{ required: true, message: 'Seleziona data/ora di inizio' }]}
                  getValueFromEvent={(d: Dayjs | null) => (d ? d.toISOString() : undefined)}
                  getValueProps={(v?: string) => ({ value: v ? dayjs(v) : undefined })}
                >
                  <DatePicker
                    showTime={{ format: 'HH:mm' }}
                    format="DD/MM/YYYY HH:mm"
                    style={{ width: '100%', height: 40, borderRadius: 6 }}
                    placeholder="Data/ora inizio"
                    onChange={(d) => {
                      if (!d) return;
                      const end = d.add(2, 'hour');
                      form.setFieldsValue({
                        appointment: {
                          ...(form.getFieldValue(['appointment']) || {}),
                          startAt: d.toISOString(),
                          endAt: end.toISOString(),
                        },
                      });
                    }}
                  />
                </Form.Item>

                <Form.Item
                  name={['appointment', 'endAt']}
                  label={<span style={{ fontWeight: 600, fontSize: 14 }}>Fine</span>}
                  rules={[{ required: true, message: 'Seleziona data/ora di fine' }]}
                  getValueFromEvent={(d: Dayjs | null) => (d ? d.toISOString() : undefined)}
                  getValueProps={(v?: string) => ({ value: v ? dayjs(v) : undefined })}
                >
                  <DatePicker
                    showTime={{ format: 'HH:mm' }}
                    format="DD/MM/YYYY HH:mm"
                    style={{ width: '100%', height: 40, borderRadius: 6 }}
                    placeholder="Data/ora fine"
                  />
                </Form.Item>
              </div>

              <Form.Item
                name={['appointment', 'confirmed']}
                label={<span style={{ fontWeight: 600, fontSize: 14 }}>Confermato</span>}
                valuePropName="checked"
                initialValue
              >
                <Switch />
              </Form.Item>

              <Form.Item
                name={['appointment', 'videomakerId']}
                label={<span style={{ fontWeight: 600, fontSize: 14 }}>Videomaker</span>}
                rules={[{ required: true, message: 'Seleziona il videomaker' }]}
              >
                <Select
                  showSearch
                  allowClear
                  placeholder="Seleziona videomaker"
                  options={videomakerOptions}
                  loading={loadingVideomakers}
                />
              </Form.Item>
            </div>
          </>
        )}

        {/* Cliente */}
        <Form.Item
          name="customerId"
          label={<span style={{ fontWeight: 600, fontSize: 14 }}>Cliente</span>}
          rules={[{ required: true, message: 'Seleziona un cliente' }]}
        >
          <Select
            showSearch
            placeholder="Seleziona un cliente"
            options={customers}
            loading={loadingCustomers}
            disabled={customerLocked}
            filterOption={(input, option) =>
              (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
            }
          />
        </Form.Item>

        {watchedType === 'EXTRA' && (
          <Form.Item
            name="name"
            label={<span style={{ fontWeight: 600, fontSize: 14 }}>Titolo</span>}
            rules={[{ required: true, message: 'Inserisci un titolo' }]}
          >
            <Input placeholder="Es. Consegna materiali" />
          </Form.Item>
        )}

        <Form.Item
          name="description"
          label={<span style={{ fontWeight: 600, fontSize: 14 }}>Descrizione</span>}
        >
          <TextArea rows={4} placeholder="Aggiungi una descrizione (opzionale)" />
        </Form.Item>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
          <Form.Item
            name="visibility"
            label={<span style={{ fontWeight: 600, fontSize: 14 }}>Visibilità</span>}
            valuePropName="checked"
            initialValue
          >
            <Switch
              checkedChildren={<EyeOutlined />}
              unCheckedChildren={<EyeInvisibleOutlined />}
            />
          </Form.Item>

          <Form.Item
            name="occurredAt"
            label={<span style={{ fontWeight: 600, fontSize: 14 }}>Data/ora</span>}
            getValueFromEvent={(d: Dayjs | null) => (d ? d.toISOString() : undefined)}
            getValueProps={(v?: string) => ({ value: v ? dayjs(v) : undefined })}
            tooltip="Per Shooting è sincronizzata all'orario di inizio"
          >
            <DatePicker
              showTime={{ format: 'HH:mm' }}
              format="DD/MM/YYYY HH:mm"
              style={{ width: '100%' }}
              placeholder="Seleziona data/ora"
              disabled={isShooting}
            />
          </Form.Item>
        </div>

        {/* ▼ Campi override SOLO se EXTRA */}
        {isExtra && (
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            <Form.Item
              name="durationMinutes"
              label={<span style={{ fontWeight: 600, fontSize: 14 }}>Durata (minuti)</span>}
              rules={[
                {
                  required: true,
                  message: 'Inserisci un costo',
                  type: 'number',
                  min: 0,
                  transform: (v) => (v === '' ? undefined : Number(v)),
                },
              ]}
            >
              <InputNumber style={{ width: '100%' }} min={0} step={5} placeholder="Es. 60" />
            </Form.Item>
            <Form.Item
              name="cost"
              label={<span style={{ fontWeight: 600, fontSize: 14 }}>Costo (€)</span>}
              rules={[
                {
                  required: true,
                  message: 'Inserisci un costo',
                  type: 'number',
                  min: 0,
                  transform: (v) => (v === '' ? undefined : Number(v)),
                },
              ]}
            >
              <InputNumber style={{ width: '100%' }} min={0} step={1} placeholder="Es. 50" />
            </Form.Item>
          </div>
        )}

        {/* Upload in CREATE */}
        {mode === 'create' && (
          <Form.Item label="Allegati iniziali">
            <Upload.Dragger
              multiple
              fileList={files}
              beforeUpload={() => false}
              onChange={(info) => setFiles(info.fileList)}
              itemRender={(originNode) => originNode}
            >
              <p className="ant-upload-drag-icon">
                <InboxOutlined />
              </p>
              <p className="ant-upload-text">Trascina qui i file o clicca per selezionarli</p>
            </Upload.Dragger>
          </Form.Item>
        )}
      </Form>

      {/* FileManager anche in EDIT */}
      {mode === 'edit' && activityId && (
        <div style={{ marginTop: 16 }}>
          <FileManager
            entity="activity"
            entityId={activityId}
            title="Allegati Attività"
            pageSize={6}
            accept="image/*,.pdf,.doc,.docx,.xls,.xlsx,.txt"
          />
        </div>
      )}
    </Drawer>
  );
};

export default CreateActivityDrawer;
